import {
	useEffect,
	useId,
	useLayoutEffect,
	useRef,
	useState,
	type ReactNode,
	type RefObject,
} from 'react';
import { ChevronRight } from 'lucide-react';
import { createPortal } from 'react-dom';
import { TutorialDemonstracaoCoachBackdrop } from './TutorialDemonstracaoCoachBackdrop';
import {
	COACH_SPOTLIGHT_RING_CLASS,
	computeTutorialHeaderNavSpotlight,
	isTutorialHeaderInteractionTarget,
	toCoachSpotlightRect,
	TUTORIAL_DEMO_HEADER_LAYOUT_SELECTORS,
} from './tutorialDemonstracaoCoachSpotlight';

const DEFAULT_BALLOON_WIDTH_PX = 280;
const BALLOON_GAP_PX = 12;
const MULTI_TARGET_BALLOON_GAP_PX = 24;
const MULTI_TARGET_DOWN_ARROW_INSET_PX = 24;
const MULTI_TARGET_TRAY_ARROW_ALIGN_OFFSET_PX = 20;

const getTrayDownArrowTargetX = (traySpotlight: SpotlightRect) =>
	traySpotlight.left + MULTI_TARGET_TRAY_ARROW_ALIGN_OFFSET_PX;

export const TUTORIAL_DEMO_MEETING_TIMER_SELECTOR =
	'[data-tutorial-demo="meeting-timer"]';

export const TUTORIAL_DEMO_PRONTUARIO_OPEN_SELECTORS = [
	'[data-tutorial-demo="prontuario-open-tray"]',
	'[data-tutorial-demo="prontuario-open-badge"]',
] as const;

export const TUTORIAL_DEMO_PRONTUARIO_CONTENT_SELECTOR =
	'[data-tutorial-demo="prontuario-content"]';

export const TUTORIAL_DEMO_PRONTUARIO_HEADER_SELECTOR =
	'[data-tutorial-demo="prontuario-header"]';

export const TUTORIAL_DEMO_PRONTUARIO_PATIENT_CARD_SELECTOR =
	'[data-tutorial-demo="prontuario-patient-card"]';

export const TUTORIAL_DEMO_PRONTUARIO_PATIENT_DETAIL_SELECTOR =
	'[data-tutorial-demo="prontuario-patient-detail"]';

export const TUTORIAL_DEMO_PRONTUARIO_SESSIONS_OPEN_SELECTOR =
	'[data-tutorial-demo="prontuario-sessions-open"]';

export const TUTORIAL_DEMO_PRONTUARIO_SESSIONS_LIST_SELECTOR =
	'[data-tutorial-demo="prontuario-sessions-list"]';

export const TUTORIAL_DEMO_PRONTUARIO_NOTES_SELECTOR =
	'[data-tutorial-demo="prontuario-notes"]';

export const TUTORIAL_DEMO_PRONTUARIO_FORMATTING_HELP_SELECTOR =
	'[data-tutorial-demo="prontuario-formatting-help"]';

export const TUTORIAL_DEMO_END_CALL_SELECTOR =
	'[data-tutorial-demo="end-call"]';

export const TUTORIAL_DEMO_END_CONFIRM_MODAL_SELECTOR =
	'[data-tutorial-demo="end-confirm-modal"]';

export const TUTORIAL_DEMO_REVIEW_SESSION_FIELDS_SELECTOR =
	'[data-tutorial-demo="review-session-fields"]';

export const TUTORIAL_DEMO_REVIEW_FIRST_ADD_TAG_SELECTOR =
	'[data-tutorial-demo="review-first-add-tag"]';

export const TUTORIAL_DEMO_REVIEW_TAG_EDITOR_SELECTOR =
	'[data-tutorial-demo="review-tag-editor"]';

export const TUTORIAL_DEMO_REVIEW_ADVANCE_SELECTOR =
	'[data-tutorial-demo="review-advance"]';

export const TUTORIAL_DEMO_REVIEW_RECORD_FIELDS_SELECTOR =
	'[data-tutorial-demo="review-record-fields"]';

export const TUTORIAL_DEMO_REVIEW_EVOLUIR_MODAL_SELECTOR =
	'[data-tutorial-demo="review-evoluir-modal"]';

const isVisibleElement = (element: HTMLElement) => {
	const rect = element.getBoundingClientRect();
	if (rect.width < 1 || rect.height < 1) return false;

	let node: HTMLElement | null = element;
	while (node) {
		if (window.getComputedStyle(node).display === 'none') return false;
		node = node.parentElement;
	}

	return true;
};

type SpotlightRect = {
	top: number;
	left: number;
	width: number;
	height: number;
};

const resolveTargetElements = (
	targetRef: RefObject<HTMLElement | null> | undefined,
	targetSelector: string | undefined,
	targetSelectors: readonly string[] | undefined,
) => {
	if (targetSelectors?.length) {
		const elements: HTMLElement[] = [];

		for (const selector of targetSelectors) {
			for (const element of document.querySelectorAll<HTMLElement>(
				selector,
			)) {
				if (isVisibleElement(element)) elements.push(element);
			}
		}

		return elements;
	}

	if (targetSelector) {
		for (const element of document.querySelectorAll<HTMLElement>(
			targetSelector,
		)) {
			if (isVisibleElement(element)) return [element];
		}
		return [];
	}

	const refElement = targetRef?.current;
	return refElement && isVisibleElement(refElement) ? [refElement] : [];
};

const toSpotlightRect = (element: HTMLElement): SpotlightRect =>
	toCoachSpotlightRect(element);

const resolveAllowedTargetSelectors = (
	targetSelector: string | undefined,
	targetSelectors: readonly string[] | undefined,
	allowTargetInteraction: boolean,
	additionalInteractionSelectors: readonly string[] | undefined,
	backdropTargetSelector: string | undefined,
) => {
	if (!allowTargetInteraction) return [];

	const selectors: string[] = [];

	if (backdropTargetSelector) selectors.push(backdropTargetSelector);

	if (targetSelectors?.length) selectors.push(...targetSelectors);
	else if (targetSelector) selectors.push(targetSelector);

	if (additionalInteractionSelectors?.length) {
		selectors.push(...additionalInteractionSelectors);
	}

	return selectors;
};

const clamp = (value: number, min: number, max: number) =>
	Math.min(Math.max(value, min), max);

const computeMultiTargetBalloonLeft = (
	traySpotlight: SpotlightRect,
	balloonWidth: number,
) => {
	const downArrowTargetX = getTrayDownArrowTargetX(traySpotlight);

	return clamp(
		downArrowTargetX - MULTI_TARGET_DOWN_ARROW_INSET_PX,
		12,
		window.innerWidth - balloonWidth - 12,
	);
};

const computeMultiTargetArrows = (
	traySpotlight: SpotlightRect,
	badgeSpotlight: SpotlightRect,
	balloonLeft: number,
	balloonBottomY: number,
	balloonHeight: number,
	balloonWidth: number,
): BalloonArrow[] => {
	const downArrowTargetX = getTrayDownArrowTargetX(traySpotlight);
	const badgeCenterY = badgeSpotlight.top + badgeSpotlight.height / 2;
	const balloonTopY = balloonBottomY - balloonHeight;

	return [
		{
			direction: 'down',
			offset: clamp(
				downArrowTargetX - balloonLeft,
				24,
				balloonWidth - 24,
			),
		},
		{
			direction: 'right',
			offset: clamp(badgeCenterY - balloonTopY, 20, balloonHeight - 20),
		},
	];
};

const BalloonPointer = ({ direction, offset }: BalloonArrow) => {
	if (direction === 'right') {
		return (
			<div
				className="absolute -right-[7px] size-0 border-y-[9px] border-l-[9px] border-y-transparent border-l-white"
				style={{ top: offset - 9 }}
				aria-hidden
			/>
		);
	}

	if (direction === 'left') {
		return (
			<div
				className="absolute -left-[7px] size-0 border-y-[9px] border-r-[9px] border-y-transparent border-r-white"
				style={{ top: offset - 9 }}
				aria-hidden
			/>
		);
	}

	return (
		<div
			className="absolute -bottom-[7px] size-0 border-x-[9px] border-t-[9px] border-x-transparent border-t-white"
			style={{ left: offset - 9 }}
			aria-hidden
		/>
	);
};

type BalloonArrow = {
	direction: 'down' | 'right' | 'left';
	offset: number;
};

type BalloonPosition = {
	top: number;
	left: number;
	arrowLeft: number;
};

type BalloonPlacement = 'above' | 'below' | 'left' | 'right';
type BalloonViewportPin = 'left' | 'right';
type BalloonPinVariant = 'viewport-edge' | 'beside-target';

type Props = {
	targetRef?: RefObject<HTMLElement | null>;
	targetSelector?: string;
	targetSelectors?: readonly string[];
	bodyId: string;
	balloonWidthPx?: number;
	compact?: boolean;
	placement?: BalloonPlacement;
	/** Fixa o balão na lateral da tela (não acompanha o topo do alvo ao rolar). */
	pinBalloonToViewport?: BalloonViewportPin;
	/** Com `pinBalloonToViewport`, tenta posicionar ao lado do alvo se couber. */
	pinBalloonBesideTargetWhenFits?: boolean;
	allowTargetInteraction?: boolean;
	additionalInteractionSelectors?: readonly string[];
	backdropTargetSelector?: string;
	showTargetSpotlight?: boolean;
	showBackdrop?: boolean;
	showContinueButton?: boolean;
	portalZIndex?: number;
	allowHeaderInteraction?: boolean;
	onTargetContinue?: () => void;
	onContinue: () => void;
	children: ReactNode;
};

const TutorialDemonstracaoCallCoachBalloon = ({
	targetRef,
	targetSelector,
	targetSelectors,
	bodyId,
	balloonWidthPx = DEFAULT_BALLOON_WIDTH_PX,
	compact = false,
	placement = 'above',
	pinBalloonToViewport,
	pinBalloonBesideTargetWhenFits = false,
	allowTargetInteraction = false,
	additionalInteractionSelectors,
	backdropTargetSelector,
	showTargetSpotlight = true,
	showBackdrop = true,
	showContinueButton = true,
	portalZIndex = 300,
	allowHeaderInteraction = true,
	onTargetContinue,
	onContinue,
	children,
}: Props) => {
	const backdropMaskId = useId().replace(/:/g, '');
	const portalRef = useRef<HTMLDivElement>(null);
	const balloonShellRef = useRef<HTMLDivElement>(null);
	const [spotlights, setSpotlights] = useState<SpotlightRect[]>([]);
	const [backdropSpotlights, setBackdropSpotlights] = useState<
		SpotlightRect[]
	>([]);
	const [headerNavSpotlight, setHeaderNavSpotlight] =
		useState<SpotlightRect | null>(null);
	const [balloonPosition, setBalloonPosition] =
		useState<BalloonPosition | null>(null);
	const [multiArrows, setMultiArrows] = useState<BalloonArrow[]>([]);
	const [leftPlacementArrowTop, setLeftPlacementArrowTop] = useState<
		number | null
	>(null);
	const [balloonPinVariant, setBalloonPinVariant] =
		useState<BalloonPinVariant | null>(null);

	useLayoutEffect(() => {
		const updateLayout = () => {
			const targets = resolveTargetElements(
				targetRef,
				targetSelector,
				targetSelectors,
			);
			if (targets.length === 0) {
				setSpotlights([]);
				setBackdropSpotlights([]);
				setBalloonPosition(null);
				setMultiArrows([]);
				return;
			}

			const spotlightRects = targets.map(toSpotlightRect);
			const backdropRects = backdropTargetSelector
				? Array.from(
						document.querySelectorAll<HTMLElement>(
							backdropTargetSelector,
						),
				  )
						.filter(isVisibleElement)
						.map(toSpotlightRect)
				: [];
			const isMultiTarget = spotlightRects.length > 1;
			const primarySpotlight = spotlightRects[0];
			const primaryCenterX =
				primarySpotlight.left + primarySpotlight.width / 2;
			const minSpotlightTop = Math.min(
				...spotlightRects.map((spotlight) => spotlight.top),
			);
			const gapPx = isMultiTarget
				? MULTI_TARGET_BALLOON_GAP_PX
				: BALLOON_GAP_PX;

			if (pinBalloonToViewport) {
				const viewportMargin = 20;
				const centeredY = clamp(
					100,
					window.innerHeight / 2,
					window.innerHeight - 100,
				);
				const targetRight =
					primarySpotlight.left + primarySpotlight.width;
				const besideLeft = targetRight + gapPx;
				const fitsBesideTarget =
					pinBalloonBesideTargetWhenFits &&
					pinBalloonToViewport === 'right' &&
					besideLeft + balloonWidthPx <=
						window.innerWidth - viewportMargin;

				setSpotlights(spotlightRects);
				setBackdropSpotlights(backdropRects);

				if (fitsBesideTarget) {
					setBalloonPinVariant('beside-target');
					setBalloonPosition({
						top: centeredY,
						left: besideLeft,
						arrowLeft: 0,
					});
					return;
				}

				setBalloonPinVariant('viewport-edge');
				setBalloonPosition({
					top: centeredY,
					left:
						pinBalloonToViewport === 'right'
							? window.innerWidth - viewportMargin
							: viewportMargin,
					arrowLeft: 0,
				});
				return;
			}

			setBalloonPinVariant(null);

			if (placement === 'left') {
				const centerY =
					primarySpotlight.top + primarySpotlight.height / 2;

				setSpotlights(spotlightRects);
				setBackdropSpotlights(backdropRects);
				setBalloonPosition({
					top: centerY,
					left: clamp(
						primarySpotlight.left - gapPx,
						balloonWidthPx + 12,
						window.innerWidth - 12,
					),
					arrowLeft: 0,
				});
				return;
			}

			if (placement === 'right') {
				const centerY =
					primarySpotlight.top + primarySpotlight.height / 2;

				setSpotlights(spotlightRects);
				setBackdropSpotlights(backdropRects);
				setBalloonPosition({
					top: centerY,
					left: clamp(
						primarySpotlight.left + primarySpotlight.width + gapPx,
						12,
						window.innerWidth - balloonWidthPx - 12,
					),
					arrowLeft: 0,
				});
				return;
			}

			const left = isMultiTarget
				? computeMultiTargetBalloonLeft(
						primarySpotlight,
						balloonWidthPx,
				  )
				: clamp(
						primaryCenterX - balloonWidthPx / 2,
						12,
						window.innerWidth - balloonWidthPx - 12,
				  );
			const top =
				placement === 'below'
					? primarySpotlight.top + primarySpotlight.height + gapPx
					: (isMultiTarget ? primarySpotlight.top : minSpotlightTop) -
					  gapPx;
			const arrowLeft = isMultiTarget
				? clamp(
						getTrayDownArrowTargetX(primarySpotlight) - left,
						MULTI_TARGET_DOWN_ARROW_INSET_PX,
						balloonWidthPx - MULTI_TARGET_DOWN_ARROW_INSET_PX,
				  )
				: primaryCenterX - left;

			setSpotlights(spotlightRects);
			setBackdropSpotlights(backdropRects);
			setBalloonPosition({
				top,
				left,
				arrowLeft: clamp(arrowLeft, 20, balloonWidthPx - 20),
			});
		};

		updateLayout();

		const observer = new ResizeObserver(updateLayout);
		const selectors = [
			...(targetSelectors?.length
				? targetSelectors
				: targetSelector
				  ? [targetSelector]
				  : []),
			...(backdropTargetSelector ? [backdropTargetSelector] : []),
		];

		for (const selector of selectors) {
			document
				.querySelectorAll<HTMLElement>(selector)
				.forEach((element) => observer.observe(element));
		}

		if (targetRef?.current) {
			observer.observe(targetRef.current);
		}

		const mutationObserver =
			selectors.length > 0 ? new MutationObserver(updateLayout) : null;
		if (mutationObserver) {
			mutationObserver.observe(document.body, {
				childList: true,
				subtree: true,
				attributes: true,
			});
		}

		window.addEventListener('resize', updateLayout);
		window.addEventListener('scroll', updateLayout, true);

		return () => {
			observer.disconnect();
			mutationObserver?.disconnect();
			window.removeEventListener('resize', updateLayout);
			window.removeEventListener('scroll', updateLayout, true);
		};
	}, [
		backdropTargetSelector,
		balloonWidthPx,
		pinBalloonBesideTargetWhenFits,
		pinBalloonToViewport,
		placement,
		targetRef,
		targetSelector,
		targetSelectors,
	]);

	useLayoutEffect(() => {
		const updateHeaderNavSpotlight = () => {
			setHeaderNavSpotlight(computeTutorialHeaderNavSpotlight());
		};

		updateHeaderNavSpotlight();

		const observer = new ResizeObserver(updateHeaderNavSpotlight);
		const mutationObserver = new MutationObserver(updateHeaderNavSpotlight);

		for (const selector of TUTORIAL_DEMO_HEADER_LAYOUT_SELECTORS) {
			document
				.querySelectorAll<HTMLElement>(selector)
				.forEach((element) => observer.observe(element));
		}

		mutationObserver.observe(document.body, {
			childList: true,
			subtree: true,
			attributes: true,
		});

		window.addEventListener('resize', updateHeaderNavSpotlight);
		window.addEventListener('scroll', updateHeaderNavSpotlight, true);

		return () => {
			observer.disconnect();
			mutationObserver.disconnect();
			window.removeEventListener('resize', updateHeaderNavSpotlight);
			window.removeEventListener(
				'scroll',
				updateHeaderNavSpotlight,
				true,
			);
		};
	}, []);

	useLayoutEffect(() => {
		if (
			placement === 'left' ||
			placement === 'right' ||
			balloonPinVariant === 'beside-target'
		) {
			if (!balloonPosition || !balloonShellRef.current) {
				setLeftPlacementArrowTop(null);
				return;
			}

			const balloonHeight =
				balloonShellRef.current.getBoundingClientRect().height;
			setLeftPlacementArrowTop(
				clamp(balloonHeight / 2, 20, balloonHeight - 20),
			);
			setMultiArrows([]);
			return;
		}

		setLeftPlacementArrowTop(null);

		if (
			spotlights.length < 2 ||
			!balloonPosition ||
			!balloonShellRef.current
		) {
			setMultiArrows([]);
			return;
		}

		const balloonHeight =
			balloonShellRef.current.getBoundingClientRect().height;
		setMultiArrows(
			computeMultiTargetArrows(
				spotlights[0],
				spotlights[1],
				balloonPosition.left,
				balloonPosition.top,
				balloonHeight,
				balloonWidthPx,
			),
		);
	}, [
		balloonPinVariant,
		balloonPosition,
		balloonWidthPx,
		placement,
		spotlights,
	]);

	const allowedTargetSelectors = resolveAllowedTargetSelectors(
		targetSelector,
		targetSelectors,
		allowTargetInteraction,
		additionalInteractionSelectors,
		backdropTargetSelector,
	);

	useEffect(() => {
		if (!showBackdrop) return;

		const isAllowedInteractionTarget = (node: Node) => {
			const continueButton = portalRef.current?.querySelector(
				'button[aria-label="Continuar"]',
			);
			if (continueButton?.contains(node)) return true;
			if (
				allowHeaderInteraction &&
				isTutorialHeaderInteractionTarget(node)
			) {
				return true;
			}

			for (const selector of allowedTargetSelectors) {
				for (const element of document.querySelectorAll(selector)) {
					if (element.contains(node)) return true;
				}
			}

			return false;
		};

		const blockDisallowedInteraction = (event: Event) => {
			const target = event.target;
			if (!(target instanceof Node)) return;
			if (isAllowedInteractionTarget(target)) return;

			event.preventDefault();
			event.stopPropagation();
		};

		document.addEventListener(
			'pointerdown',
			blockDisallowedInteraction,
			true,
		);
		document.addEventListener('click', blockDisallowedInteraction, true);

		return () => {
			document.removeEventListener(
				'pointerdown',
				blockDisallowedInteraction,
				true,
			);
			document.removeEventListener(
				'click',
				blockDisallowedInteraction,
				true,
			);
		};
	}, [allowedTargetSelectors, allowHeaderInteraction, showBackdrop]);

	useEffect(() => {
		if (!onTargetContinue) return;

		const selectors = targetSelectors?.length
			? [...targetSelectors]
			: targetSelector
			  ? [targetSelector]
			  : [];
		if (selectors.length === 0) return;

		const handleClick = (event: MouseEvent) => {
			const clicked = event.target;
			if (!(clicked instanceof Node)) return;

			for (const selector of selectors) {
				for (const element of document.querySelectorAll(selector)) {
					if (element.contains(clicked)) {
						event.preventDefault();
						event.stopPropagation();
						onTargetContinue();
						return;
					}
				}
			}
		};

		document.addEventListener('click', handleClick, true);
		return () => document.removeEventListener('click', handleClick, true);
	}, [onTargetContinue, targetSelector, targetSelectors]);

	if (spotlights.length === 0 || !balloonPosition) {
		return createPortal(
			<div
				ref={portalRef}
				className="pointer-events-none fixed inset-0"
				style={{ zIndex: portalZIndex }}
				aria-hidden
			/>,
			document.body,
		);
	}

	const singleArrow = [
		{ direction: 'down' as const, offset: balloonPosition.arrowLeft },
	];
	const arrows = pinBalloonToViewport
		? []
		: spotlights.length > 1
		  ? multiArrows.length > 0
				? multiArrows
				: singleArrow
		  : singleArrow;

	const holeSpotlights = backdropTargetSelector
		? backdropSpotlights
		: spotlights;
	const ringSpotlights = showTargetSpotlight
		? spotlights
		: backdropTargetSelector
		  ? backdropSpotlights
		  : spotlights;

	const backdropHoles =
		headerNavSpotlight && allowHeaderInteraction
			? [...holeSpotlights, headerNavSpotlight]
			: holeSpotlights;

	return createPortal(
		<div
			ref={portalRef}
			className="pointer-events-none fixed inset-0"
			style={{ zIndex: portalZIndex }}
		>
			{showBackdrop ? (
				<TutorialDemonstracaoCoachBackdrop
					maskId={backdropMaskId}
					holes={backdropHoles}
				/>
			) : null}

			{showBackdrop
				? ringSpotlights.map((spotlight) => (
						<div
							key={`${spotlight.top}-${spotlight.left}-${spotlight.width}-${spotlight.height}`}
							className={COACH_SPOTLIGHT_RING_CLASS}
							style={{
								top: spotlight.top,
								left: spotlight.left,
								width: spotlight.width,
								height: spotlight.height,
							}}
							aria-hidden
						/>
				  ))
				: null}

			<div
				className="pointer-events-auto absolute"
				style={{
					top: balloonPosition.top,
					left: balloonPosition.left,
					width: balloonWidthPx,
					transform:
						balloonPinVariant === 'beside-target'
							? 'translate(0, -50%)'
							: pinBalloonToViewport === 'right'
							  ? 'translate(-100%, -50%)'
							  : pinBalloonToViewport === 'left'
							    ? 'translate(0, -50%)'
							    : placement === 'above'
							      ? 'translateY(-100%)'
							      : placement === 'left'
							        ? 'translate(-100%, -50%)'
							        : placement === 'right'
							          ? 'translate(0, -50%)'
							          : undefined,
				}}
				role="dialog"
				aria-labelledby={bodyId}
			>
				{placement === 'below' ? (
					<div
						className="absolute -top-[7px] size-0 border-x-[9px] border-b-[9px] border-x-transparent border-b-white"
						style={{ left: balloonPosition.arrowLeft - 9 }}
						aria-hidden
					/>
				) : null}
				{placement === 'left' && leftPlacementArrowTop !== null ? (
					<BalloonPointer
						direction="right"
						offset={leftPlacementArrowTop}
					/>
				) : null}
				{placement === 'right' && leftPlacementArrowTop !== null ? (
					<BalloonPointer
						direction="left"
						offset={leftPlacementArrowTop}
					/>
				) : null}
				{balloonPinVariant === 'beside-target' &&
				leftPlacementArrowTop !== null ? (
					<BalloonPointer
						direction="left"
						offset={leftPlacementArrowTop}
					/>
				) : null}
				<div
					ref={balloonShellRef}
					className={
						compact
							? 'rounded-xl border border-zinc-200/90 bg-white px-4 py-2 shadow-2xl'
							: 'rounded-xl border border-zinc-200/90 bg-white px-4 py-3 shadow-2xl'
					}
				>
					<div
						className={
							showContinueButton
								? 'grid grid-cols-[minmax(0,1fr)_auto] items-end gap-x-2 gap-y-1'
								: undefined
						}
					>
						<div
							id={bodyId}
							className={
								compact
									? 'text-sm leading-snug text-zinc-600'
									: 'text-sm leading-relaxed text-zinc-600'
							}
						>
							{children}
						</div>
						{showContinueButton ? (
							<button
								type="button"
								onClick={onContinue}
								className="flex size-7 shrink-0 items-center justify-center self-end rounded-full bg-zinc-900 text-white transition-colors hover:bg-zinc-800"
								aria-label="Continuar"
							>
								<ChevronRight
									className="size-3.5"
									strokeWidth={2.5}
								/>
							</button>
						) : null}
					</div>
				</div>
				{placement === 'above'
					? arrows.map((arrow) => (
							<BalloonPointer
								key={`${arrow.direction}-${arrow.offset}`}
								direction={arrow.direction}
								offset={arrow.offset}
							/>
					  ))
					: null}
			</div>
		</div>,
		document.body,
	);
};

export { TutorialDemonstracaoCallCoachBalloon };
